import type { FingeringSize } from '../components/FingeringOverlay';
import type { PitchScope } from '../model/pitchVisibility';

interface StaffEntry {
  mainFinger: string[];
  altFinger?: string[];
  mainCritical: boolean;
  altCritical: boolean;
  repeatGroup: {
    measureIndex: number;
    indexInMeas: number;
  }[];
  flipGroup?: {
    measureIndex: number;
    indexInMeas: number;
  }[];
}

type StaffData = StaffEntry[];

type MeasureData = StaffData[];

type FingeringData = MeasureData[];

/** Which hand a single-part score is played with. */
type Hand = 'right' | 'left';

/**
 * How many staves the uploaded score turned out to have. A `grand_staff`
 * score carries the hand in its staff index; a `single_part` score has one
 * staff and relies on the hand the user picked instead.
 */
type ScoreLayout = 'grand_staff' | 'single_part';

/**
 * Persisted per-project view preferences: which overlays are visible plus
 * the fingering font size. The edit/pencil mode is never persisted.
 */
interface DisplaySettings {
  showAltFingerings: boolean;
  showAccidental: boolean;
  showPitch: boolean;
  /**
   * Which notes get a letter while `showPitch` is on. `all` is every
   * note; `ledger` is notes with two or more ledger lines. Turning
   * pitch off keeps the last scope.
   */
  pitchScope: PitchScope;
  showFingerings: boolean;
  fontSize: FingeringSize;
  /** Pitch-letter size. Small keeps letters on the noteheads. */
  pitchFontSize: FingeringSize;
}

/**
 * Defaults applied when a project has no persisted settings (legacy /
 * freshly created). Pitch defaults OFF; fingerings and the other overlays
 * default ON; font size defaults to medium.
 */
const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  showAltFingerings: true,
  showAccidental: true,
  showPitch: false,
  pitchScope: 'all',
  showFingerings: true,
  fontSize: 'medium',
  pitchFontSize: 'small',
};

export type {
  StaffEntry,
  StaffData,
  MeasureData,
  FingeringData,
  DisplaySettings,
  Hand,
  ScoreLayout,
};
export { DEFAULT_DISPLAY_SETTINGS };
